import { useMemo } from 'react';
import { Flex } from '@servicetitan/anvil2';
import getDisplayName from '@lib/utils/getDisplayName';
import datetime from '@lib/utils/datetime';
import useService from '@modules/ServiceModule/useService';
import TasksModule from '@modules/TasksModule';
import useVoiceTask from '@modules/TasksModule/hooks/useVoiceTask';
import useTenantName from '@lib/hooks/useTenantName';
import { pipLocators } from '@locators/pip';
import { getTestIdProps } from '@lib/testUtils';
import styles from '../styles/pip.module.css';

interface PipCallCardProps {
  readonly callId: string;
}

export function PipCallCard({ callId }: PipCallCardProps) {
  const { userByContactCenterUserId } = useService();
  const task = TasksModule.getTaskByCallId(callId);
  const { isIncomingTransfer } = useVoiceTask(task);
  const tenantId = task?.tenantId ?? '';
  const tenantName = useTenantName(tenantId);

  const callerName = useMemo(
    () => getDisplayName(task?.displayName ?? '', userByContactCenterUserId),
    [task?.displayName, userByContactCenterUserId],
  );

  if (!task) return null;

  const workflowName = task.attributes?.workflow?.name;
  const queueName = task.queueName;
  const startTime = datetime(task.startedAt).format('h:mm A');

  return (
    <Flex
      direction='column'
      className={styles.callCard}
      {...getTestIdProps(pipLocators.callInfo)}
    >
      <Flex justifyContent='space-between' alignItems='center'>
        <span className={styles.callCardName}>{callerName}</span>
        <span className={styles.callCardTime}>{startTime}</span>
      </Flex>

      {tenantName && (
        <span className={styles.callCardTenant}>{tenantName}</span>
      )}

      {isIncomingTransfer && (
        <span className={styles.transferTag}>Transfer</span>
      )}

      <Flex direction='column' gap='half'>
        {workflowName && (
          <Flex gap='1' className={styles.callCardDetail}>
            <span className={styles.callCardLabel}>Workflow:</span>
            <span className={styles.callCardValue}>{workflowName}</span>
          </Flex>
        )}
        {queueName && (
          <Flex gap='1' className={styles.callCardDetail}>
            <span className={styles.callCardLabel}>Queue:</span>
            <span className={styles.callCardValue}>{queueName}</span>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
